import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { PRESETS, DEFAULT_IMPACT, money, parseAmount } from "@/lib/format";

const METHODS = ["Card", "Apple Pay", "Google Pay", "PayPal", "Venmo", "Zelle", "Crypto"];
const AMOUNT_ERROR = "Please enter an amount of $1 or more.";
const CHECKOUT_ERROR = "We couldn't open checkout just now. Please try again, or give with PayPal below.";

function MonthlyToggle({ on, onChange }) {
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-2xl bg-[var(--pg-soft)] p-3 sm:p-4">
      <button type="button" role="switch" aria-checked={on} aria-labelledby="monthly-label" onClick={() => onChange(!on)}
        className="inline-flex h-11 w-16 shrink-0 items-center justify-center rounded-full" data-testid="monthly-donation-toggle">
        <span className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${on ? "bg-[#24453D]" : "bg-[#8B8478]"}`}>
          <span className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-7" : "translate-x-1"}`} />
        </span>
      </button>
      <div className="min-w-0 flex-1 pt-2.5">
        <p id="monthly-label" className="cursor-pointer font-semibold" onClick={() => onChange(!on)}>Make it monthly <span className="font-normal text-[#4A5160]">({on ? "on" : "off"})</span></p>
        <p className="text-sm text-[#3B4A44]" data-testid="monthly-cancel-note">Monthly gifts can be stopped anytime. Just email or call us.</p>
      </div>
    </div>
  );
}

function AmountPicker({ choice, setChoice }) {
  return (
    <div role="radiogroup" aria-label="Choose an amount" className="grid grid-cols-3 gap-2 sm:gap-3">
      {PRESETS.map((p) => {
        const on = choice === String(p.amount);
        return (
          <button key={p.amount} type="button" role="radio" aria-checked={on} onClick={() => setChoice(String(p.amount))}
            className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-2xl border-2 p-3 text-center sm:min-h-[92px] sm:items-start sm:justify-start sm:p-4 sm:text-left transition-colors ${
              on ? "border-[#24453D] bg-[var(--pg-soft)]" : "border-[#D9CFC3] bg-white hover:border-[#24453D]/60"}`}
            data-testid={`donation-amount-${p.amount}`}>
            <span className="font-display text-2xl font-semibold">${p.amount}</span>
            <span className="hidden text-sm leading-snug text-[#3B4A44] sm:block">${p.amount} {p.impact}</span>
          </button>
        );
      })}
    </div>
  );
}

export function DonationWidget({ returnPath = "/", level: Heading = "h3" }) {
  const { search } = useLocation();
  const cancelled = new URLSearchParams(search).get("checkout") === "cancelled";
  const [choice, setChoice] = useState("50");
  const [other, setOther] = useState("");
  const [monthly, setMonthly] = useState(true);
  const [amountError, setAmountError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cancelled) setTimeout(() => document.getElementById("give")?.scrollIntoView(), 50);
  }, [cancelled]);

  const isOther = choice === "other";
  const amount = isOther ? parseAmount(other) : Number(choice);
  const impact = isOther ? `Your gift ${DEFAULT_IMPACT}.` : `$${choice} ${PRESETS.find((p) => String(p.amount) === choice).impact}`;

  const give = async () => {
    setCheckoutError("");
    if (!amount) return setAmountError(AMOUNT_ERROR);
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", { amount, monthly, origin_url: window.location.origin, return_path: returnPath });
      window.location.href = data.url;
    } catch {
      setCheckoutError(CHECKOUT_ERROR);
      setBusy(false);
    }
  };

  return (
    <div className="of-card p-5 sm:p-8" data-testid="donation-widget">
      {cancelled && (
        <p role="status" className="mb-6 rounded-2xl bg-[#E8F3EC] px-4 py-3 font-medium text-[#1E5B3D]" data-testid="checkout-cancelled-message">
          No problem. Nothing was charged.
        </p>
      )}
      <Heading className="font-display text-xl font-semibold sm:text-2xl">Choose an amount</Heading>
      <p className="mt-1 hidden text-[#3B4A44] sm:block">Take your time. Every amount helps.</p>
      <div className="mt-4"><AmountPicker choice={choice} setChoice={(c) => { setChoice(c); setOther(""); setAmountError(""); }} /></div>

      <div className="mt-3 max-w-xs">
        <label htmlFor="other-amount" className="of-label">Other amount</label>
        <div className="relative">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-semibold text-[#3B4A44]">$</span>
          <input id="other-amount" inputMode="decimal" autoComplete="off" className="of-input pl-8" value={other} placeholder="Any amount"
            onFocus={() => setChoice("other")} onChange={(e) => { setOther(e.target.value); setChoice("other"); setAmountError(""); }}
            aria-invalid={!!amountError} aria-describedby="other-amount-error" data-testid="donation-custom-amount-input" />
        </div>
        <p id="other-amount-error" className="of-error min-h-[1.25rem]" aria-live="polite" data-testid="donation-amount-error">{amountError}</p>
      </div>

      <div className="mt-1"><MonthlyToggle on={monthly} onChange={setMonthly} /></div>

      <p className="mt-4 font-display text-xl font-semibold text-[#1E2B27]" aria-live="polite" data-testid="donation-impact-sentence">{impact}</p>
      <div className="mt-3 flex flex-col gap-3 xl:flex-row xl:items-center">
        <button type="button" onClick={give} disabled={busy} className="of-btn-donate min-h-[56px] w-full whitespace-nowrap px-8 text-lg xl:w-auto xl:shrink-0" data-testid="pay-button-stripe">
          <CreditCard className="h-5 w-5" aria-hidden />
          {busy ? "Opening secure checkout…" : `Give ${amount ? money(amount) : ""}${monthly ? " monthly" : ""}`.replace("Give  ", "Give ")}
        </button>
        <p className="flex items-start gap-2 text-sm text-[#3B4A44]" data-testid="trust-line">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          Registered 501(c)(3) · 100% goes to the mission · Tax-deductible · Secure checkout by Stripe
        </p>
      </div>
      {checkoutError && <p role="alert" className="of-error mt-4 text-base" data-testid="checkout-error-message">{checkoutError}</p>}
      <p className="mt-4 text-sm text-[#4A5160]">No account needed. Apple Pay and Google Pay show up at checkout on devices that support them.</p>
      <p className="mt-6 text-sm font-semibold text-[#3B4A44]">Ways to give</p>
      <ul className="mt-2 flex flex-wrap gap-2" data-testid="ways-to-give-list">
        {METHODS.map((m) => (
          <li key={m} className="rounded-full border border-[#D9CFC3] bg-white px-3 py-1 text-sm font-medium">{m}</li>
        ))}
      </ul>
    </div>
  );
}
